import { FetchArgs } from "@reduxjs/toolkit/query/react";

import { Role } from "../apis";
import { ModifyQueryByRoleParams } from "./types";

/**
 * Modifies query URL based on user role for multi-tenant access control
 *
 * @param params - Role-based modification parameters
 * @returns Modified query args or null if request should be aborted
 */
export const modifyQueryByRole = ({ role, original, modified, originalArgs, abortIfRoleNotFound = false }: ModifyQueryByRoleParams): FetchArgs | string | null => {
  // If originalArgs is null, return null
  if (originalArgs === null) {
    return null;
  }

  // Find the modified URL for the current role
  const modifiedUrl = modified[role];

  // If no modification exists for this role
  if (!modifiedUrl) {
    // If abortIfRoleNotFound is true, return null to signal request should be aborted
    if (abortIfRoleNotFound) {
      return null;
    }
    // Otherwise, return the original query
    return originalArgs;
  }

  // If the query is a simple string
  if (typeof originalArgs === "string") {
    if (originalArgs === original || originalArgs.startsWith(`${original}?`)) {
      return modifiedUrl;
    }
    return originalArgs;
  }

  // If the query is a FetchArgs object
  if (originalArgs.url === original || originalArgs.url?.startsWith(`${original}?`)) {
    return {
      ...originalArgs,
      url: modifiedUrl
    };
  }

  return originalArgs;
};

/**
 * Applies role-based URL modifications to request arguments
 *
 * @param args - Original request arguments
 * @param userRole - Current user's role
 * @param companyId - Company ID for Usuario role
 * @param shippingCompanyId - Shipping company ID for Transportadora role
 * @param userId - User ID for specific user-based routes
 * @returns Modified request arguments or null if request should be aborted
 */
export const applyRoleBasedModifications = (
  args: FetchArgs | string,
  userRole: Role | undefined,
  companyId: number | undefined,
  shippingCompanyId: number | undefined,
  userId: number | undefined
): FetchArgs | string | null => {
  if (!userRole) {
    return args;
  }

  let modifiedArgs: FetchArgs | string | null = args;

  // Modify order routes for Transportadora role
  modifiedArgs = modifyQueryByRole({
    role: userRole,
    original: `/order/company/${userId}`,
    modified: {
      [Role.Transportadora]: `/order/shipping-company/${shippingCompanyId}`
    },
    originalArgs: modifiedArgs
  });

  // Modify customer routes for Usuario role
  modifiedArgs = modifyQueryByRole({
    role: userRole,
    original: "/customer",
    modified: {
      [Role.Usuario]: `/customer/company/${companyId}`
    },
    originalArgs: modifiedArgs
  });

  // Modify product routes for Usuario role
  modifiedArgs = modifyQueryByRole({
    role: userRole,
    original: "/product",
    modified: {
      [Role.Usuario]: `/product/company/${companyId}`
    },
    originalArgs: modifiedArgs
  });

  return modifiedArgs;
};
